/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { RoleAction, RoleId } from '@wix/members-domain-ts';

import roleIcons from '../../constants/role-icons';
import withTranslate from '../../hoc/with-translate';
import { preventDefault } from '../../services/click-handling';
import styles from './popover-content.scss';
import { getIsMobile } from '../../state/site/selectors';
import { RoleActionIcon } from './role-action-icon';
import { RoleActionLink } from './role-action-link';
import { RoleActionLinkText } from './role-action-link-text';
import { PopoverContentSeparator } from './popover-content-separator';

interface Props {
  rolesActions: RoleAction[];
  member: any;
  onRoleAction: Function;
  t: any;
  isMobile: any;
}

interface RenderRoleActionProps {
  roleId: string;
  action: string;
}

export const PopoverContent: React.StatelessComponent<Props> = ({
  rolesActions,
  member,
  onRoleAction,
  t,
  isMobile,
}) => {
  const RoleActionItem: React.StatelessComponent<RenderRoleActionProps> = ({ roleId, action }) => {
    const onClick = preventDefault(() => onRoleAction(member.uid, { roleId }));

    return (
      <RoleActionLink isMenuItem={true} key={action} dataHook={action} onClick={onClick}>
        <RoleActionIcon>{roleId}</RoleActionIcon>
        <RoleActionLinkText>{t(action)}</RoleActionLinkText>
      </RoleActionLink>
    );
  };

  const roleIconsKeys = Object.keys(roleIcons);
  const withIcons = rolesActions.filter(({ roleId }) => roleIconsKeys.includes(roleId));
  let withoutIcons = rolesActions.filter(({ roleId }) => !roleIconsKeys.includes(roleId));
  const isSeparatorNeeded = withIcons.length > 0 && withoutIcons.length > 0;

  // Render "Manage badges" at the end if it's there
  const manageBadgesAction = withoutIcons.find((r) => r.roleId === RoleId.MANAGE_BADGES);
  if (manageBadgesAction) {
    withoutIcons = withoutIcons.filter((r) => r !== manageBadgesAction);
    withoutIcons.push(manageBadgesAction);
  }

  const rolesActionsWithIcons = withIcons.map((r) => (
    <RoleActionItem key={r.action} action={r.action} roleId={r.roleId} />
  ));
  const rolesActionsWithoutIcons = withoutIcons.map((r) => (
    <RoleActionItem key={r.action} action={r.action} roleId={r.roleId} />
  ));

  // Add separator before "Manage badges" if there are more roles without icons
  if (manageBadgesAction && rolesActionsWithoutIcons.length > 1) {
    rolesActionsWithoutIcons.splice(
      rolesActionsWithoutIcons.length - 1,
      0,
      <PopoverContentSeparator key="PopoverContentSeparator-1" />,
    );
  }

  const allItems = [
    ...rolesActionsWithIcons,
    isSeparatorNeeded && <PopoverContentSeparator key="PopoverContentSeparator-2" />,
    ...rolesActionsWithoutIcons,
  ].filter((i) => !!i);

  const classes = [
    styles.actionsContainer,
    rolesActions.length > 1 ? styles.actionsContainerManyItems : null,
    rolesActions.length <= 1 ? styles.actionsContainerOneItem : null,
    { [styles.mobile]: isMobile },
  ].filter((x) => x);
  return <div className={classNames(...classes)}>{allItems}</div>;
};

export default connect((state) => ({
  isMobile: getIsMobile(state),
}))(withTranslate(PopoverContent));
